import Disclaimer from "./Disclaimer";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";

function XLogo({ className, size = 16 }: { className?: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function DiscordLogo({ className, size = 16 }: { className?: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6173-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
    </svg>
  );
}

export default function Footer() {
  return (
    <footer className="border-t border-border/40 mt-16 sm:mt-20">
      <div className="container py-10 grid gap-10 md:gap-8 md:grid-cols-4">
        {/* Brand column */}
        <div className="md:col-span-1 flex flex-col items-center md:items-start">
          <div className="font-display text-lg font-bold tracking-tight">
            Alea<span className="te-grad-text">Edge</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-[240px] text-center md:text-left">
            Premium AI sports analytics. The Top 5 daily plays, every day.
          </p>

          {/* Social icons — inline under brand */}
          <div className="mt-5 flex items-center gap-2.5">
            <a
              href="https://x.com/aleaedge"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AleaEdge on X"
              className="group inline-grid place-items-center w-9 h-9 rounded-xl border border-border/50 bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/10 hover:-translate-y-0.5 hover:shadow-[0_0_16px_hsl(152_83%_48%_/0.25)] transition-all duration-300"
            >
              <XLogo size={16} className="transition-opacity duration-300 group-hover:opacity-100 opacity-70" />
            </a>
            <a
              href="https://instagram.com/aleaedge"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AleaEdge on Instagram"
              className="group inline-grid place-items-center w-9 h-9 rounded-xl border border-border/50 bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/10 hover:-translate-y-0.5 hover:shadow-[0_0_16px_hsl(152_83%_48%_/0.25)] transition-all duration-300"
            >
              <Instagram size={16} strokeWidth={2} className="transition-opacity duration-300 group-hover:opacity-100 opacity-70" />
            </a>
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="AleaEdge on Discord"
              className="group inline-grid place-items-center w-9 h-9 rounded-xl border border-border/50 bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/10 hover:-translate-y-0.5 hover:shadow-[0_0_16px_hsl(152_83%_48%_/0.25)] transition-all duration-300"
            >
              <DiscordLogo size={16} className="transition-opacity duration-300 group-hover:opacity-100 opacity-70" />
            </a>
          </div>
        </div>

        {/* Product */}
        <div className="text-sm space-y-2">
          <div className="font-semibold text-foreground mb-2">Product</div>
          <Link to="/dashboard" className="block text-muted-foreground hover:text-foreground transition-colors">Dashboard</Link>
          <Link to="/top-5" className="block text-muted-foreground hover:text-foreground transition-colors">Top 5 Plays</Link>
          <Link to="/picks" className="block text-muted-foreground hover:text-foreground transition-colors">All Picks</Link>
          <Link to="/history" className="block text-muted-foreground hover:text-foreground transition-colors">Pick History</Link>
          <Link to="/pricing" className="block text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
        </div>

        {/* Company */}
        <div className="text-sm space-y-2">
          <div className="font-semibold text-foreground mb-2">Company</div>
          <Link to="/" className="block text-muted-foreground hover:text-foreground transition-colors">About</Link>
          <a href="mailto:support@aleaedge.com" className="block text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          <Link to="/pricing#faq" className="block text-muted-foreground hover:text-foreground transition-colors">FAQ</Link>
        </div>

        {/* Legal */}
        <div className="text-sm space-y-2">
          <div className="font-semibold text-foreground mb-2">Legal</div>
          <Link to="/terms" className="block text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
          <Link to="/privacy" className="block text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
          <Link to="/billing-terms" className="block text-muted-foreground hover:text-foreground transition-colors">Billing Terms</Link>
          <Link to="/refund-policy" className="block text-muted-foreground hover:text-foreground transition-colors">Refund Policy</Link>
          <Link to="/responsible-gambling" className="block text-muted-foreground hover:text-foreground transition-colors">Responsible Gambling</Link>
        </div>
      </div>

      <div className="container pb-8">
        <div className="text-xs text-muted-foreground border-t border-border/40 pt-5">
          <Disclaimer compact />
        </div>
        <div className="text-xs text-muted-foreground/70 mt-3">
          &copy; {new Date().getFullYear()} AleaEdge &middot; Informational analytics only. 21+ &middot; Please bet responsibly.
        </div>
      </div>
    </footer>
  );
}
